import api from 'api';
import axios from 'axios';
import { CORE_API, HUB_SIGNIN } from 'config';
import { ORIGINAL_USER_LOCAL_STORAGE_KEY } from 'globalConstants';
import AuthService, { Role } from 'services/auth-service';
import { sessionMonitor } from 'services/session';
import { trackSignIn } from 'services/sign-in-tracking';
import { computeCodeChallenge, generateCodeVerifier, generateState } from 'utils/pkce';
import { HUB_CALLBACK_PATH } from './hub-callback';
import {
  HubErrorResponse,
  HubSignInErrorCode,
  HubSignInResult,
  HubTokenExchangeResponse,
  LegacySession,
} from './hub-auth-service.types';

export { HUB_CALLBACK_PATH };
export const VERIFIER_KEY = 'hubSignIn.codeVerifier';
export const STATE_KEY = 'hubSignIn.state';

const TOKEN_TIMEOUT_MS = 10_000;
const END_SESSION_TIMEOUT_MS = 4_000;

/**
 * A bare instance: not `api()`, which sends the stored Core token, and not the
 * default instance, whose session interceptor would read a failure as an expiry.
 */
const tokenClient = axios.create({ timeout: TOKEN_TIMEOUT_MS });
const coreSessionClient = axios.create({ timeout: END_SESSION_TIMEOUT_MS });

/** Off unless the flag is on and both Hub URLs are set. */
export const isHubSignInEnabled = (): boolean =>
  HUB_SIGNIN.enabled && !!HUB_SIGNIN.signInUrl && !!HUB_SIGNIN.authApiUrl;

export const HUB_SIGNIN_ERROR_MESSAGES: Record<HubSignInErrorCode, string> = {
  denied: 'Sign-in with Enara Hub was cancelled or denied. Please try again.',
  state_mismatch: 'The sign-in response could not be verified. Please try again.',
  expired: 'The sign-in link expired or was already used. Please try again.',
  network: 'Could not reach Enara Hub. Check your connection and try again.',
  unavailable: 'Sign-in with Enara Hub is not available right now. Please try again later.',
  no_access: "Your account doesn't have access to the Provider App. Contact Tech Support.",
  start_failed: 'Could not start sign-in with Enara Hub. Please try again.',
};

export const isHubSignInErrorCode = (value: unknown): value is HubSignInErrorCode =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(HUB_SIGNIN_ERROR_MESSAGES, value);

export class HubSignInError extends Error {
  readonly code: HubSignInErrorCode;

  constructor(code: HubSignInErrorCode) {
    super(HUB_SIGNIN_ERROR_MESSAGES[code]);
    this.name = 'HubSignInError';
    this.code = code;
  }
}

const redirectUri = (): string => `${window.location.origin}${HUB_CALLBACK_PATH}`;

/** Sends the browser to the Hub sign-in page, with PKCE and a state to check on return. */
export const startHubSignIn = async (): Promise<void> => {
  if (!isHubSignInEnabled()) {
    throw new HubSignInError('start_failed');
  }

  const verifier = generateCodeVerifier();
  const state = generateState();
  const challenge = await computeCodeChallenge(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const url = new URL(HUB_SIGNIN.signInUrl as string);
  url.searchParams.set('applicationSlug', HUB_SIGNIN.applicationSlug);
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  window.location.assign(url.toString());
};

const isRole = (role: unknown): role is Role => {
  const candidate = role as Role | null;
  return (
    typeof candidate?.name === 'string' &&
    (typeof candidate.id === 'number' || typeof candidate.id === 'string')
  );
};

/** The Core user id as stored by a password sign-in: a number. */
const coreUserIdOf = (value: unknown): number | undefined => {
  const id = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 ? id : undefined;
};

const isLegacySession = (value: unknown): value is LegacySession => {
  const session = value as LegacySession | null | undefined;
  const expiresAtMs = Date.parse(session?.expiresAt ?? '');
  return (
    typeof session?.authToken === 'string' &&
    session.authToken.length > 0 &&
    coreUserIdOf(session.coreUserId) !== undefined &&
    !Number.isNaN(expiresAtMs) &&
    expiresAtMs > Date.now() &&
    Array.isArray(session.roles) &&
    session.roles.every(isRole)
  );
};

const exchangeCode = async (code: string, verifier: string): Promise<HubTokenExchangeResponse> => {
  try {
    const response = await tokenClient.post<HubTokenExchangeResponse>(
      `${HUB_SIGNIN.authApiUrl}/auth/token`,
      {
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
        application_slug: HUB_SIGNIN.applicationSlug,
      }
    );
    return response.data;
  } catch (error) {
    const response = (error as { response?: { status?: number; data?: HubErrorResponse } })
      ?.response;
    if (response?.status === undefined) {
      throw new HubSignInError('network');
    }
    const hubError = response.data?.error?.details?.error;
    // invalid_grant: the code expired (60s) or was already used. Any other 400
    // (a rejected request) will not go away by signing in again.
    if (response.status === 400 && hubError === 'invalid_grant') {
      throw new HubSignInError('expired');
    }
    // The Hub refused this account, not an outage. It never says why.
    if (response.status === 403 && hubError === 'legacy_session_forbidden') {
      throw new HubSignInError('no_access');
    }
    throw new HubSignInError('unavailable');
  }
};

/**
 * Ends a Hub-issued session in Core (S1). Never waited for and never fails:
 * signing out locally must not depend on it. A 401 means it had already ended.
 */
export const endCoreSession = (token: string): Promise<void> =>
  coreSessionClient
    .delete(`${CORE_API.url}/v1/sessions/current`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(
      () => undefined,
      () => undefined
    );

const establishSession = (
  session: LegacySession,
  userId: number,
  previousHubToken: string | null
): void => {
  // Written first: if storage refuses it, the session stored before is untouched.
  AuthService.setAuth({ auth_token: session.authToken, user_id: userId, roles: session.roles });
  // Whatever else was stored belongs to an earlier session, including an impersonation.
  AuthService.clearSessionMarkers();
  localStorage.removeItem(ORIGINAL_USER_LOCAL_STORAGE_KEY);
  AuthService.setHubToken(session.authToken);
  sessionMonitor.reset();
  sessionMonitor.setExpiry(session.expiresAt);

  // The Hub session this one replaced is ended in Core only (S1): this sign-in
  // may hang off the same Hub session, and the Hub logout would end it, and
  // this new Core session with it.
  if (previousHubToken && previousHubToken !== session.authToken) {
    endCoreSession(previousHubToken);
  }
};

/**
 * Core's password sign-in returns the user's uuid, but here it comes from the
 * profile. Best-effort: the boot fetches the profile again, so a failure must
 * not undo the sign-in. Never rejects.
 */
const trackHubSignIn = async (userId: number, roles: Role[]): Promise<void> => {
  try {
    const {
      data: { user },
    } = await api().get('users/AUTH_ID', { params: { auth_token: 'AUTH_TOKEN' } });
    trackSignIn({ uuid: user.uuid, userId, roles });
  } catch (error) {
    // Tracking only.
  }
};

const runHubSignIn = async (search: string): Promise<HubSignInResult> => {
  const params = new URLSearchParams(search);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  // Single-use, whatever the outcome.
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);

  const code = params.get('code');
  const hubError = params.get('error');
  if (!code && !hubError && !verifier) {
    return 'nothing-to-complete';
  }
  if (hubError || !code) {
    throw new HubSignInError('denied');
  }
  if (!verifier || !expectedState || params.get('state') !== expectedState) {
    throw new HubSignInError('state_mismatch');
  }

  const body = await exchangeCode(code, verifier);
  const session = body?.data?.legacySession;
  if (!isLegacySession(session)) {
    throw new HubSignInError('unavailable');
  }

  const userId = coreUserIdOf(session.coreUserId) as number;
  const previousHubToken = AuthService.getHubToken();
  try {
    establishSession(session, userId, previousHubToken);
  } catch (error) {
    // Never leave half a session behind, nor sign out one this did not replace.
    const replaced = AuthService.getAuth()?.Authorization.token === session.authToken;
    AuthService.removeAuthIfToken(session.authToken);
    sessionMonitor.reset();
    // Nothing holds the new session any more, nor the one it overwrote: end them in Core.
    endCoreSession(session.authToken);
    if (replaced && previousHubToken && previousHubToken !== session.authToken) {
      endCoreSession(previousHubToken);
    }
    throw new HubSignInError('unavailable');
  }

  // Not waited for: the session is stored, and the profile request has no timeout.
  trackHubSignIn(userId, session.roles);
  return 'signed-in';
};

let inFlight: Promise<HubSignInResult> | null = null;

/**
 * Completes the sign-in the Hub redirected back with. Single-flight: a callback
 * page that remounts (with the code already stripped from the URL) joins the
 * exchange in progress instead of starting an empty one, since a code is
 * single-use.
 */
export const completeHubSignIn = (search: string): Promise<HubSignInResult> => {
  if (!inFlight) {
    const attempt = runHubSignIn(search).finally(() => {
      if (inFlight === attempt) {
        inFlight = null;
      }
    });
    inFlight = attempt;
  }
  return inFlight;
};

/**
 * Signs out of the Hub session a Hub-issued token belongs to, which ends the
 * Core session too. Fire-and-forget: `keepalive` lets the request outlive the
 * page, and it never rejects. The Hub answers 204 whether or not it knows the
 * token, so anything else (429, 5xx), or no answer, ends the Core session
 * directly instead (S1).
 */
export const endHubSession = (token: string): Promise<void> => {
  const endInCore = () => endCoreSession(token);
  if (!HUB_SIGNIN.authApiUrl) {
    return endInCore();
  }
  try {
    return fetch(`${HUB_SIGNIN.authApiUrl}/auth/legacy-session/logout`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ applicationSlug: HUB_SIGNIN.applicationSlug, authToken: token }),
    }).then((response) => (response.ok ? undefined : endInCore()), endInCore);
  } catch (error) {
    // fetch can throw synchronously (e.g. a keepalive body over the browser limit).
    return endInCore();
  }
};
